import { DEFAULT_LOCALE, getLocaleRules, isNumeric, type Locale, type LocaleInput } from './locale';
import { formatNumberByLocale } from './numbers';
import type { WeightUnit } from './weight';

const WEIGHT_UNIT_LABELS: Record<WeightUnit, string> = {
    kg: 'kg',
    lb: 'lbs',
};

const DEFAULT_WEIGHT_UNIT: WeightUnit = 'kg';

const UNIVERSAL_UNIT_SPACING_LABELS: string[] = ['BL/s', 'KL/s', 'LC/s', 'm', 'Hz'];

export const SWIM_SPEED_UNIT_LABEL: Record<Locale, string> = {
    en: 'BL/s',
    nb: 'KL/s',
    es: 'LC/s',
};

export function getWeightUnitLabel(weightUnit: WeightUnit): string {
    return WEIGHT_UNIT_LABELS[weightUnit];
}

export function getSwimSpeedUnitLabelByLocale(locale: Locale): string {
    return SWIM_SPEED_UNIT_LABEL[locale];
}

/** "1,234.56 kg" / "1 234,56 kg"; `fallback` for anything that is not a number. */
export function formatWeightByLocale(
    value: unknown,
    unit: WeightUnit = DEFAULT_WEIGHT_UNIT,
    locale: LocaleInput = DEFAULT_LOCALE,
    decimals = 2,
    fallback = '-'
): string {
    if (!isNumeric(value)) return fallback;
    const rules = getLocaleRules(locale);
    const formattedNumber = formatNumberByLocale(Number(value), locale, decimals);
    return `${formattedNumber}${rules.weightUnitSpacing}${getWeightUnitLabel(unit)}`;
}

/** Swim speed in body lengths per second, with the locale's own unit label. */
export function formatSwimSpeedByLocale(
    value: unknown,
    locale: Locale = DEFAULT_LOCALE,
    decimals = 2,
    fallback = '-'
): string {
    if (!isNumeric(value)) return fallback;
    const rules = getLocaleRules(locale);
    const formattedNumber = formatNumberByLocale(Number(value), locale, decimals);
    return `${formattedNumber}${rules.universalUnitSpacing}${getSwimSpeedUnitLabelByLocale(locale)}`;
}

/** A percentage given in percent (25.5 for 25.5 %): "25.5%" / "25,5 %". */
export function formatPercentageByLocale(
    value: unknown,
    locale: LocaleInput = DEFAULT_LOCALE,
    decimals = 1,
    fallback = '-'
): string {
    if (!isNumeric(value)) return fallback;
    const rules = getLocaleRules(locale);
    const formattedNumber = formatNumberByLocale(Number(value), locale, decimals);
    return `${formattedNumber}${rules.percentageSpacing}%`;
}

/** A percentage given as a fraction (0.255 for 25.5 %), as most rates arrive from the API. */
export function formatFractionAsPercentageByLocale(
    value: number | null | undefined,
    locale: LocaleInput = DEFAULT_LOCALE,
    decimals = 1,
    fallback = '-'
): string {
    if (value == null) return fallback;
    return formatPercentageByLocale(value * 100, locale, decimals, fallback);
}

/** "22.5°C" / "22,5 °C". */
export function formatTemperatureByLocale(
    value: unknown,
    unit = '°C',
    locale: LocaleInput = DEFAULT_LOCALE,
    decimals = 1,
    fallback = '-'
): string {
    if (!isNumeric(value)) return fallback;
    const rules = getLocaleRules(locale);
    const formattedNumber = formatNumberByLocale(Number(value), locale, decimals);
    return `${formattedNumber}${rules.temperatureSpacing}${unit}`;
}

function getUnitSpacingByLocale(unit: string, locale: LocaleInput): string {
    const rules = getLocaleRules(locale);
    if (UNIVERSAL_UNIT_SPACING_LABELS.includes(unit)) return rules.universalUnitSpacing;
    if (unit === '°C') return rules.temperatureSpacing;
    if (unit === '°') return rules.tiltSpacing;
    if (unit === '%') return rules.percentageSpacing;
    return rules.universalUnitSpacing;
}

/** Any value with a unit after it, spaced the way the locale spaces that unit. */
export function formatUniversalUnitByLocale(
    value: unknown,
    unit: string,
    locale: LocaleInput = DEFAULT_LOCALE,
    decimals = 1,
    fallback = '-'
): string {
    if (!isNumeric(value)) return fallback;
    const formattedNumber = formatNumberByLocale(Number(value), locale, decimals);
    return `${formattedNumber}${getUnitSpacingByLocale(unit, locale)}${unit}`;
}
